import React, { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import { authAPI } from '../services/api';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isAuthenticated, setIsAuthenticated] = useState(false);

  const checkAuth = useCallback(async () => {
    console.log('🔍 [AuthContext] checkAuth called');

    try {
      console.log('🔍 [AuthContext] Making checkAuth API call...');
      const response = await authAPI.checkAuth();
      console.log('✅ [AuthContext] checkAuth response:', response.data);

      const userData = response.data.data.user;

      if (userData) {
        setUser(userData);
        setIsAuthenticated(true);
      } else {
        console.log('❌ [AuthContext] No user data in response');
        setUser(null);
        setIsAuthenticated(false);
      }
    } catch (error) {
      console.log("this is the error :: ", error)
      console.log('❌ [AuthContext] checkAuth error:', error.response?.status, error.response?.data);
      setUser(null);
      setIsAuthenticated(false);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = useCallback(async (credentials) => {
    try {
      const response = await authAPI.login(credentials);
      const userData = response.data.data.user;

      setUser(userData);
      setIsAuthenticated(true);

      // Handle language preference (keep this - it's separate from auth)
      const lang = userData?.preferences?.language || localStorage.getItem('i18nextLng') || 'en';
      if (window.i18n) window.i18n.changeLanguage(lang);
      localStorage.setItem('i18nextLng', lang);

      return { success: true, user: userData };
    } catch (error) {
      throw error;
    }
  }, []);

  const logout = useCallback(async () => {
    try {
      await authAPI.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Always clear local state
      setUser(null);
      setIsAuthenticated(false);
    }
  }, []);

  // ✅ Update user data
  const updateUser = useCallback((newUser) => {
    setUser(newUser);
  }, []);

  const refreshAuth = useCallback(async () => {
    await checkAuth();
  }, [checkAuth]);

  const value = useMemo(() => {
    const isSuperAdmin = user?.isSuperAdmin === true;

    const hasPrivilege = (module, operation) => {
      if (isSuperAdmin) return true;
      return user?.privileges?.some(p =>
        p.module === module && Array.isArray(p.operations) && p.operations.includes(operation)
      );
    };

    return {
      user,
      loading,
      isAuthenticated,
      isSuperAdmin,
      login,
      logout,
      updateUser,
      refreshAuth,
      hasPrivilege,
      userType: user?.userType,
      canAdd: hasPrivilege('appointments', 'create'),
      canEdit: hasPrivilege('appointments', 'update'),
      canDelete: hasPrivilege('appointments', 'delete'),
      hasViewPrivilege: hasPrivilege('appointments', 'view'),
      canAccessPatients: hasPrivilege('patients', 'view'),
      canAccessDoctors: hasPrivilege('doctors', 'view'),
      canAccessUsers: hasPrivilege('users', 'view'),
      canAccessBranches: hasPrivilege('branches', 'view'),
      canAccessScans: hasPrivilege('scans', 'view'),
      canAccessRepresentatives: hasPrivilege('representatives', 'view'),
      canAccessTechnicians: hasPrivilege('radiologyTechnicians', 'view'),
      canAccessRadiologists: hasPrivilege('radiologists', 'view'),
      canAccessDoctorSpecialization: hasPrivilege('doctorSpecialization', 'view'),
      canAccessScanCategories: hasPrivilege('scanCategories', 'view'),
    };
  }, [user, loading, isAuthenticated, login, logout, updateUser, refreshAuth]);

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};